// ============================================================================
// JARVISTRAVEL - COLLABORATION API
// Version: 1.0.0
// Trip collaboration — invite, list, accept/decline, remove
// ============================================================================

import { api, isDemoSession } from "./client";
import type { TripMember } from "@/app/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CollaboratorData {
  id: string;
  trip_id: string;
  user_id?: string;
  invited_email: string;
  permission: "view" | "edit";
  status: "pending" | "accepted" | "declined";
  invited_by: string;
  invited_at: string;
  responded_at?: string;
}

export interface InviteCollaboratorRequest {
  email: string;
  permission: "view" | "edit";
}

export interface ListCollaboratorsResponse {
  collaborators: CollaboratorData[];
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * Invite a collaborator to a trip.
 * POST /v1/trips/{trip_id}/collaborators
 */
export async function inviteCollaborator(
  tripId: string,
  email: string,
  permission: "view" | "edit",
): Promise<CollaboratorData> {
  // The demo's invitation is made here and sent nowhere (JAR-1996): pending,
  // as core answers a new one.
  if (isDemoSession()) {
    return {
      id: `demo-invite-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
      trip_id: tripId,
      invited_email: email,
      permission,
      status: "pending",
      invited_by: "demo",
      invited_at: new Date().toISOString(),
    };
  }
  const result = await api.post<CollaboratorData>(`/trips/${encodeURIComponent(tripId)}/collaborators`, {
    email,
    permission,
  });

  if (result.error) {
    // The client's own error, so the screen can tell a trip that has ended
    // from a failure worth retrying (isTripLocked, JAR-1864).
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

/** How the person an invitation was sent to answers it. */
export type InvitationAnswer = "accept" | "decline";

/**
 * Accept or decline an invitation, as the person it was sent to.
 * POST /v1/trips/{trip_id}/collaborators/{id}/accept or /decline
 */
export async function answerInvitation(
  tripId: string,
  collaboratorId: string,
  answer: InvitationAnswer,
): Promise<CollaboratorData> {
  // The ids come from a link someone followed, so each stays inside its own
  // path segment whatever it holds.
  const result = await api.post<CollaboratorData>(
    `/trips/${encodeURIComponent(tripId)}/collaborators/${encodeURIComponent(collaboratorId)}/${answer}`,
  );

  if (result.error) {
    // The client's own error, with core's status and code, so the screen can
    // tell another account, an answered or withdrawn invitation, and a failure
    // worth retrying apart.
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}

/**
 * List collaborators for a trip.
 * GET /v1/trips/{trip_id}/collaborators
 */
export async function listCollaborators(
  tripId: string,
  status?: "pending" | "accepted" | "declined",
): Promise<CollaboratorData[]> {
  // The id escaped, the status not: it is a union of three literals, so escaping it
  // would be noise. Every id this file puts in a path is escaped (JAR-2049's rule).
  const path = `/trips/${encodeURIComponent(tripId)}/collaborators${status ? `?status=${status}` : ""}`;
  const result = await api.get<ListCollaboratorsResponse>(path);

  if (result.error) {
    throw new Error(result.error.message || "Failed to list collaborators");
  }

  return result.data?.collaborators || [];
}

interface TripMemberData {
  user_id: string;
  first_name: string;
  last_name: string;
  role: "owner" | "collaborator";
}

interface ListMembersResponse {
  members: TripMemberData[];
}

/**
 * The trip's members for the "Who's going" picker (JAR-2014): the owner and
 * accepted collaborators, owner first then by name, with their names but no
 * emails. Anyone who can view the trip may read it.
 * GET /v1/trips/{trip_id}/members
 */
export async function listTripMembers(tripId: string): Promise<TripMember[]> {
  const result = await api.get<ListMembersResponse>(`/trips/${encodeURIComponent(tripId)}/members`);

  if (result.error) {
    throw new Error(result.error.message || "Failed to list trip members");
  }

  return (result.data?.members ?? []).map((m) => ({
    userId: m.user_id,
    firstName: m.first_name,
    lastName: m.last_name,
    role: m.role,
  }));
}

/**
 * Remove a collaborator from a trip.
 * DELETE /v1/trips/{trip_id}/collaborators/{id}
 */
export async function removeCollaborator(
  tripId: string,
  collaboratorId: string,
): Promise<void> {
  const result = await api.del<{ success: boolean }>(
    `/trips/${encodeURIComponent(tripId)}/collaborators/${encodeURIComponent(collaboratorId)}`,
  );

  if (result.error) {
    // Raised whole, as the two writes either side of it are. Nothing reads the lock
    // off a removal today — ShareModal's handleRemove reverts silently, noted as out
    // of scope on JAR-2166 — but leaving one of the three writes flattening is the
    // same "a subset of the file follows the rule" split that the id escaping in
    // this file had (web-app#584 review).
    throw result.error;
  }
}

/**
 * Update collaborator permission.
 * PUT /v1/trips/{trip_id}/collaborators/{id}
 */
export async function updateCollaboratorPermission(
  tripId: string,
  collaboratorId: string,
  permission: "view" | "edit",
): Promise<CollaboratorData> {
  const result = await api.put<CollaboratorData>(
    `/trips/${encodeURIComponent(tripId)}/collaborators/${encodeURIComponent(collaboratorId)}`,
    { permission },
  );

  if (result.error) {
    // The client's own error, for the same reason inviteCollaborator above raises
    // it: flattened to a plain Error it loses `status` and `detailCode`, which is
    // the only way isTripLocked recognises a trip that has ended — so a screen
    // calling writeFailureMessage on it could never say core's words and would
    // always fall through to "try again", on a trip where trying again cannot work
    // (JAR-1864, web-app#584 review).
    throw result.error;
  }

  if (!result.data) {
    throw new Error("Empty response from server");
  }

  return result.data;
}
